import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowClockwise, Checks, CircleNotch, Pause, TrayArrowDown, WarningCircle } from '@phosphor-icons/react';
import {
  BestaetigenDialog, ImmoButton, LeerZustand, SchwebenderToast, SegmentedControl, useToastPill, type ObjektPickerOption,
} from '../../../../components/immobilien/ui';
import { ABLAGE_GEAENDERT } from '../../../../components/immobilien/sidebar/MobileLeiste';
import { useMobilModus } from '../../../../lib/immobilien/mobil';
import { aktualisiereVorschlag, legeVorschlagAb, markiereVorschlag } from '../../../../lib/dokumente/ablageApi';
import type { AblageSegment } from '../../../../lib/dokumente/ablage';
import { ladeObjekte, type AktObjekt } from '../../../../lib/dokumente/useAktenschrank';
import { ladeMassenKandidaten, useZurAblage, type AblageZeile } from '../../../../lib/dokumente/useZurAblage';
import { meldeDokumenteGeaendert } from '../../../../lib/dokumente/useDokumenteKopf';
import { VorschlagKarte, type KartenAktionen } from './VorschlagKarte';
import { useOrdnerQuelle } from './ordnerQuelle';

const melde = () => { meldeDokumenteGeaendert(); window.dispatchEvent(new CustomEvent(ABLAGE_GEAENDERT)); };
const fehlerText = (e: unknown, sonst: string) => (e instanceof Error && e.message ? e.message : sonst);

interface MassenStand { aktuell: number; gesamt: number; ok: number; fehler: number; ersterFehler: string | null; stopp: boolean }

/**
 * Zur Ablage (Handoff Dokumente §6.3): Segment Offen/Abgelegt/Abgelehnt, Karten je Anhang, „Alle ≥ 90 % ablegen“
 * nach Bestätigung nacheinander (anhaltbar).
 */
export function ZurAblageTab({ userId, onOeffneInAkten }: { userId: string | null; onOeffneInAkten: (objektId: string | null, dateiId: string) => void }) {
  const [segment, setSegment] = useState<AblageSegment>('offen');
  const d = useZurAblage(segment);
  const { mobil } = useMobilModus();
  const ordner = useOrdnerQuelle();
  const [toast, zeigeToast] = useToastPill(3200);

  const [objekte, setObjekte] = useState<AktObjekt[]>([]);
  useEffect(() => { ladeObjekte().then(setObjekte).catch(() => setObjekte([])); }, []);
  const pickerOptionen: ObjektPickerOption[] = useMemo(() => objekte.map(o => ({
    id: o.id, label: o.name, nummer: o.nummer, sub: [o.verwaltungsart, o.ort].filter(Boolean).join(' · '),
    suchtext: `${o.nummer} ${o.ort}`, hinweis: o.driveId ? undefined : 'kein Drive-Ordner',
  })), [objekte]);

  // ── Einzelaktionen ─────────────────────────────────────────────────────
  const aktionen: KartenAktionen = {
    onAblegen: async (z, w) => {
      const geaendert = w.objektId !== z.objekt_id || w.zielPfad !== (z.ziel_pfad ?? '') || w.dateiname !== z.dateiname;
      if (geaendert) await aktualisiereVorschlag(z.id, { objekt_id: w.objektId, ziel_pfad: w.zielPfad, dateiname: w.dateiname.trim() }).catch(() => undefined);
      if (!z.anhang) throw new Error('Anhang nicht gefunden');
      const r = await legeVorschlagAb({ vorschlagId: z.id, anhangId: z.anhang.id, objektId: w.objektId, zielPfad: w.zielPfad, dateiname: w.dateiname, userId });
      d.entferne(z.id);
      melde();
      if (r.art === 'schon-abgelegt') zeigeToast('Dieser Anhang war bereits abgelegt.');
      else if (r.statusFehler) zeigeToast('Abgelegt, aber der Status konnte nicht gespeichert werden.', true);
      else zeigeToast(`Abgelegt: ${w.dateiname.trim()}`, false, r.gdriveFileId ? { label: 'Öffnen', onClick: () => onOeffneInAkten(w.objektId, r.gdriveFileId as string) } : undefined);
    },
    onAblehnen: async z => {
      await markiereVorschlag(z.id, { status: 'abgelehnt', entschieden_von: userId });
      d.entferne(z.id);
      melde();
      zeigeToast('Vorschlag abgelehnt', false, {
        label: 'Rückgängig',
        onClick: () => { markiereVorschlag(z.id, { status: 'offen', entschieden_von: userId }).then(() => { d.neuLaden(); melde(); }).catch((e: unknown) => zeigeToast(fehlerText(e, 'Rückgängig fehlgeschlagen'), true)); },
      });
    },
    onZurueck: async z => {
      await markiereVorschlag(z.id, { status: 'offen', entschieden_von: userId });
      d.entferne(z.id);
      melde();
      zeigeToast('Zurück in der Warteschlange');
    },
    onOeffnen: z => z.gdrive_file_id && onOeffneInAkten(z.objekt_id, z.gdrive_file_id),
  };

  // ── Massenablage ───────────────────────────────────────────────────────
  const [frage, setFrage] = useState(false);
  const [massen, setMassen] = useState<MassenStand | null>(null);
  const stoppRef = useRef(false);
  const starteMassenablage = async () => {
    setFrage(false);
    stoppRef.current = false;
    setMassen({ aktuell: 0, gesamt: d.massen, ok: 0, fehler: 0, ersterFehler: null, stopp: false });
    let kandidaten: AblageZeile[];
    try { kandidaten = await ladeMassenKandidaten(); } catch (e) { setMassen(null); zeigeToast(fehlerText(e, 'Vorschläge nicht geladen'), true); return; }
    let ok = 0; let fehler = 0; let ersterFehler: string | null = null;
    for (let i = 0; i < kandidaten.length; i++) {
      if (stoppRef.current) break;
      const z = kandidaten[i];
      setMassen({ aktuell: i + 1, gesamt: kandidaten.length, ok, fehler, ersterFehler, stopp: false });
      try {
        if (!z.anhang || !z.objekt_id || !z.ziel_pfad) throw new Error('unvollständig');
        await legeVorschlagAb({ vorschlagId: z.id, anhangId: z.anhang.id, objektId: z.objekt_id, zielPfad: z.ziel_pfad, dateiname: z.dateiname, userId });
        ok++;
      } catch (e) { fehler++; ersterFehler ??= `${z.dateiname}: ${fehlerText(e, 'Ablage fehlgeschlagen')}`; }
    }
    setMassen(null);
    d.neuLaden();
    melde();
    zeigeToast(fehler ? `${ok} abgelegt, ${fehler} fehlgeschlagen – ${ersterFehler}` : `${ok} ${ok === 1 ? 'Anhang' : 'Anhänge'} abgelegt${stoppRef.current ? ' (angehalten)' : ''}`, fehler > 0);
  };
  useEffect(() => {
    if (!massen) return;
    const warnen = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warnen);
    return () => window.removeEventListener('beforeunload', warnen);
  }, [massen]);

  const z = d.zaehler;
  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14 }}>
      <div className="flex items-center flex-wrap" style={{ gap: 12 }}>
        <SegmentedControl<AblageSegment> ariaLabel="Status der Vorschläge" art="akzent" wert={segment} onChange={setSegment} hoehe={mobil ? 44 : 34}
          segmente={[{ key: 'offen', label: 'Offen', anzahl: z?.offen }, { key: 'abgelegt', label: 'Abgelegt', anzahl: z?.abgelegt }, { key: 'abgelehnt', label: 'Abgelehnt', anzahl: z?.abgelehnt }]} />
        <span className="flex-1" style={{ fontSize: 12, color: 'var(--ink3)', minWidth: 160 }}>Vorschläge aus der KI-Analyse eingehender E-Mails</span>
        {segment === 'offen' && d.massen > 0 && (
          <ImmoButton art="akzent" groesse={44} icon={Checks} disabled={!!massen} onClick={() => setFrage(true)}>Alle ≥ 90 % ablegen ({d.massen.toLocaleString('de-DE')})</ImmoButton>
        )}
      </div>

      {massen && (
        <div role="status" aria-live="polite" className="immo-glas flex flex-col" style={{ borderRadius: 22, padding: 14, gap: 8, position: 'sticky', top: 12, zIndex: 10 }}>
          <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
            <CircleNotch size={16} className="animate-spin" color="var(--acc)" />
            <span className="flex-1 immo-zahl" style={{ fontSize: 13, fontWeight: 800, minWidth: 160 }}>
              {massen.aktuell === 0 ? 'Vorschläge werden geladen …' : `Lege ab: ${massen.aktuell.toLocaleString('de-DE')} von ${massen.gesamt.toLocaleString('de-DE')}`}
              {massen.fehler > 0 && <span style={{ color: 'var(--bad)' }}> · {massen.fehler} Fehler</span>}
            </span>
            <ImmoButton groesse={44} icon={Pause} disabled={massen.stopp} onClick={() => { stoppRef.current = true; setMassen(m => (m ? { ...m, stopp: true } : m)); }}>
              {massen.stopp ? 'Hält nach dieser Datei an …' : 'Anhalten'}
            </ImmoButton>
          </div>
          <span style={{ height: 6, borderRadius: 3, background: 'var(--fill3)', overflow: 'hidden' }}>
            <span style={{ display: 'block', height: '100%', width: `${massen.gesamt ? Math.round((massen.aktuell / massen.gesamt) * 100) : 0}%`, background: 'var(--acc)', transition: 'width .3s' }} />
          </span>
          <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Bitte diese Seite geöffnet lassen, bis die Ablage fertig ist.</span>
        </div>
      )}

      {d.fehler && (
        <div role="alert" className="immo-glas flex items-center flex-wrap" style={{ borderRadius: 22, padding: 16, gap: 12 }}>
          <WarningCircle weight="fill" size={22} color="var(--bad)" />
          <span className="flex-1" style={{ fontSize: 13 }}>{d.fehler}</span>
          <ImmoButton icon={ArrowClockwise} onClick={d.neuLaden}>Erneut versuchen</ImmoButton>
        </div>
      )}

      {d.laedt ? (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 440px), 1fr))', gap: 14 }} aria-busy="true">
          {Array.from({ length: 4 }, (_, i) => <div key={i} className="animate-pulse" style={{ height: 320, borderRadius: 26, background: 'var(--fill2)' }} />)}
        </div>
      ) : d.zeilen.length === 0 ? (
        <LeerZustand icon={TrayArrowDown}
          titel={segment === 'offen' ? 'Nichts zur Ablage' : segment === 'abgelegt' ? 'Noch nichts abgelegt' : 'Nichts abgelehnt'}
          text={segment === 'offen' ? 'Neue Anhänge aus E-Mails erscheinen hier, sobald die KI sie analysiert hat.' : undefined} />
      ) : (
        <div className="grid items-start" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 440px), 1fr))', gap: 14 }}>
          {d.zeilen.map(zeile => (
            <VorschlagKarte key={zeile.id} z={zeile} objekte={objekte} pickerOptionen={pickerOptionen} ordner={ordner} aktionen={aktionen} gesperrt={!!massen} />
          ))}
        </div>
      )}
      {!d.laedt && d.mehr && (
        <ImmoButton groesse={44} icon={ArrowClockwise} onClick={() => void d.ladeMehr()} style={{ alignSelf: 'center' }}>Weitere laden</ImmoButton>
      )}

      {frage && (
        <BestaetigenDialog titel="Alle ≥ 90 % ablegen?" gefahr={false} icon={Checks} bestaetigenLabel={`${d.massen.toLocaleString('de-DE')} ablegen`}
          text="Die Anhänge werden nacheinander mit vorgeschlagenem Objekt, Ordner und Dateinamen in Google Drive abgelegt."
          detail={<span>Nur vollständige Vorschläge mit Sicherheit ab 90 %. Du kannst jederzeit anhalten.</span>}
          onBestaetigen={() => void starteMassenablage()} onAbbrechen={() => setFrage(false)} />
      )}

      <SchwebenderToast toast={toast} mobil={mobil} />
    </div>
  );
}
